/**
 * English - `account`: the My Account screen, where the signed-in person looks
 * after their own name, email, password and preferences. Grouped by card:
 * `profile.*`, `email.*`, `password.*`, `preferences.*`, `info.*`.
 * Conventions: see `common.ts`.
 */
const account: Record<string, string> = {
  title: 'My Account',
  subtitle: 'Your own sign-in and contact details. Your store settings are under Settings.',
  'errors.generic': 'Something went wrong. Try again.',

  // >>> profile
  'profile.title': 'Profile',
  'profile.description': 'How you appear to your team across the dashboard.',
  'profile.full_name': 'Full name',
  'profile.phone': 'Phone number',
  'profile.name_too_short': 'Use at least 2 characters.',
  'profile.saved': 'Saved.',

  // >>> email
  'email.title': 'Email address',
  'email.description': 'You sign in with this address, and account emails are sent here.',
  'email.verified': 'Verified',
  'email.unverified': 'Not verified',
  'email.unverified_warning':
    'This address is not confirmed yet. You will not be able to sign in again until it is.',
  'email.change': 'Change email',
  'email.add': 'Add email',
  'email.none': 'No email yet',
  'email.none_hint': 'You signed up with WhatsApp. Add an email to get receipts, invoices and password resets.',
  'email.new_email': 'New email address',
  'email.change_note':
    'We will send a 6-digit code to the new address. You stay signed in while you confirm it.',
  'email.send_code': 'Send code',
  'email.sending': 'Sending…',
  'email.code_sent': 'Enter the code we sent to {email}.',
  'email.code': 'Verification code',
  'email.verify': 'Confirm email',
  'email.verifying': 'Confirming…',
  'email.resend': 'Send a new code',
  'email.resend_in': 'Send a new code in {seconds}s',
  'email.later': 'Do this later',
  'email.verified_notice': 'Your email address is confirmed.',

  // >>> password
  'password.title': 'Password',
  'password.description': 'Changing it signs you out on every device, including this one.',
  'password.change': 'Change password',
  'password.current': 'Current password',
  'password.new': 'New password',
  'password.confirm': 'Confirm new password',
  'password.mismatch': 'These do not match.',
  'password.submit': 'Update password',

  // >>> preferences
  'push.title': 'Notifications on this device',
  'push.description': 'Get a notification on this phone or computer when a new order comes in, and when NeonCarts has news for you. Set separately on each device you use.',
  'push.enable': 'Turn on notifications',
  'push.disable': 'Turn off on this device',
  'push.new_orders': 'New orders',
  'push.new_orders_hint': 'Every order placed in your shop or on WhatsApp, as it arrives.',
  'push.platform_updates': 'NeonCarts updates',
  'push.platform_updates_hint': 'New features and important notices from the NeonCarts team.',
  'push.support_replies': 'Support replies',
  'push.support_replies_hint': 'When the NeonCarts team answers one of your support conversations.',
  'push.blocked': 'Notifications are blocked for NeonCarts in this browser. Allow them in the browser settings, then come back here.',
  'push.failed': 'Could not change notifications on this device. Try again in a moment.',
  'preferences.title': 'Preferences',
  'preferences.description': 'Only for you. Your team and your customers are not affected.',
  'preferences.language': 'Dashboard language',

  // >>> account & security information
  'info.title': 'Account & security',
  'info.description': 'Details about your account. These are set by the platform or your business owner.',
  'info.role': 'Role',
  'info.primary_owner': 'Primary owner',
  'info.status': 'Account status',
  'info.member_since': 'Member since',
  'info.last_sign_in': 'Last sign-in',
  'info.never': 'Never',
  'info.email_verification': 'Email verification',
  'info.verified_on': 'Verified on {date}',
  'info.devices': 'Signed-in devices',
  'info.device_count_one': '{count} device',
  'info.device_count_other': '{count} devices',
  'info.manage_devices': 'Manage devices and sessions',
};

export default account;
